import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Home, MapPin, Search, Sparkles, Loader2, Check, ArrowLeft, Plus } from 'lucide-react';
import { slugify } from '@/lib/slugify';

interface TownSuggestion {
  name: string;
  state: string;
  description: string;
}

interface SiteData {
  id: string;
  name: string;
  location_served: string;
}

interface ExistingTown {
  name: string;
  state: string;
}

export default function TownSetup() {
  const { user, loading: authLoading } = useAuth();
  const { selectedSiteId, loading: globalAdminLoading } = useGlobalAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [site, setSite] = useState<SiteData | null>(null);
  const [suggestions, setSuggestions] = useState<TownSuggestion[]>([]);
  const [existingTowns, setExistingTowns] = useState<ExistingTown[]>([]);
  const [selectedTowns, setSelectedTowns] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customTown, setCustomTown] = useState('');
  const [customState, setCustomState] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }

    if (user && !globalAdminLoading && selectedSiteId) {
      fetchSiteAndSuggestions();
    }
  }, [user, authLoading, globalAdminLoading, selectedSiteId, navigate]);

  const fetchSiteAndSuggestions = async () => {
    if (!selectedSiteId) {
      setLoading(false);
      return;
    }

    try {
      // Get site details using the selected site from global admin context
      const { data: siteData } = await supabase
        .from('sites')
        .select('id, name, location_served')
        .eq('id', selectedSiteId)
        .single();

      if (siteData) {
        setSite(siteData);
        
        // Fetch existing towns for this site
        const { data: existingTownsData } = await supabase
          .from('towns')
          .select('name, state')
          .eq('site_id', siteData.id);
        
        if (existingTownsData) {
          setExistingTowns(existingTownsData.map(t => ({ 
            name: t.name.toLowerCase(), 
            state: (t.state || '').toUpperCase() 
          })));
        }
        
        // Auto-fetch suggestions based on site location
        await fetchSuggestions(siteData.location_served);
      }
    } catch (error) {
      console.error('Error fetching site data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async (location: string) => {
    setSuggestionsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('suggest-towns', {
        body: { location },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setSuggestions(data.towns || []);
    } catch (error: any) {
      console.error('Error fetching suggestions:', error);
      toast({
        title: 'Error fetching suggestions',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const toggleTown = (townKey: string) => {
    const newSelected = new Set(selectedTowns);
    if (newSelected.has(townKey)) {
      newSelected.delete(townKey);
    } else {
      newSelected.add(townKey);
    }
    setSelectedTowns(newSelected);
  };

  const selectAll = () => {
    const allKeys = filteredSuggestions
      .filter(t => !isTownExisting(t.name, t.state))
      .map(t => `${t.name}|${t.state}`);
    setSelectedTowns(new Set(allKeys));
  };

  const deselectAll = () => {
    setSelectedTowns(new Set());
  };

  const isTownExisting = (name: string, state: string) => {
    return existingTowns.some(
      t => t.name === name.toLowerCase() && t.state === state.toUpperCase()
    );
  };

  const addCustomTown = () => {
    if (!customTown.trim() || !customState.trim()) {
      toast({
        title: 'Missing information',
        description: 'Please enter both town name and state.',
        variant: 'destructive',
      });
      return;
    }

    const newTown: TownSuggestion = {
      name: customTown.trim(),
      state: customState.trim().toUpperCase(),
      description: 'Custom added town',
    };

    // Check if already exists
    const exists = suggestions.some(
      t => t.name.toLowerCase() === newTown.name.toLowerCase() && t.state === newTown.state
    );

    if (!exists) {
      setSuggestions([newTown, ...suggestions]);
      setSelectedTowns(new Set([`${newTown.name}|${newTown.state}`, ...selectedTowns]));
    }

    setCustomTown('');
    setCustomState('');
  };

  const saveTowns = async () => {
    if (selectedTowns.size === 0) {
      toast({
        title: 'No towns selected',
        description: 'Please select at least one town to add.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const townsToInsert = Array.from(selectedTowns).map(key => {
        const [name, state] = key.split('|');
        const suggestion = suggestions.find(s => s.name === name && s.state === state);
        return {
          site_id: site!.id,
          name,
          state,
          slug: slugify(name),
          intro_summary: suggestion?.description || null,
          is_published: false,
        };
      });

      const { data: insertedTowns, error } = await supabase
        .from('towns')
        .insert(townsToInsert)
        .select('id');

      if (error) {
        if (error.code === '23505') {
          throw new Error('Some towns already exist. Please refresh and try again.');
        }
        throw error;
      }

      const newTownIds = insertedTowns?.map(t => t.id) || [];

      // Generate AI content for the new towns
      toast({
        title: 'Generating content...',
        description: 'AI is writing detailed content for your towns. This may take a moment.',
      });

      try {
        const { data: contentResult, error: contentError } = await supabase.functions.invoke('generate-town-content', {
          body: { townIds: newTownIds, generateImages: true },
        });

        if (contentError) {
          console.error('Content generation error:', contentError);
          toast({
            title: 'Content generation issue',
            description: 'Towns were saved but some content could not be generated. You can regenerate later.',
            variant: 'destructive',
          });
        } else {
          console.log('Content generation result:', contentResult);
          toast({
            title: 'Towns created with AI content!',
            description: `${contentResult.message}. Now select areas.`,
          });
        }
      } catch (contentErr: any) {
        console.error('Content generation failed:', contentErr);
        toast({
          title: 'Towns saved',
          description: 'Towns were saved. AI content generation is being processed.',
        });
      }

      // Navigate to area setup with the new town IDs
      navigate('/admin/areas/setup', { state: { newTownIds } });
    } catch (error: any) {
      console.error('Error saving towns:', error);
      toast({
        title: 'Error saving towns',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const filteredSuggestions = suggestions.filter(town =>
    town.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    town.state.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Home className="h-6 w-6 text-primary" />
              <span className="font-display text-xl">{site?.name || 'Town Setup'}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-accent" />
            Select Towns to Cover
          </h1>
          <p className="text-muted-foreground mt-2">
            AI-suggested towns based on your location: <strong>{site?.location_served}</strong>
          </p>
        </div>

        {/* Add Custom Town */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Add Custom Town</CardTitle>
            <CardDescription>Don't see a town? Add it manually.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Town name"
                value={customTown}
                onChange={(e) => setCustomTown(e.target.value)}
                className="flex-1"
              />
              <Input
                placeholder="State (e.g., MA)"
                value={customState}
                onChange={(e) => setCustomState(e.target.value)}
                className="w-24"
                maxLength={2}
              />
              <Button onClick={addCustomTown} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search towns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAll}>
              Select All
            </Button>
            <Button variant="outline" size="sm" onClick={deselectAll}>
              Deselect All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => site && fetchSuggestions(site.location_served)}
              disabled={suggestionsLoading}
            >
              {suggestionsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Town Suggestions */}
        {suggestionsLoading ? (
          <Card className="p-12">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-accent" />
              <p className="text-muted-foreground">AI is finding the best towns for your area...</p>
            </div>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              {filteredSuggestions.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  No towns found. Try a different search or add custom towns.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredSuggestions.map((town) => {
                    const townKey = `${town.name}|${town.state}`;
                    const isSelected = selectedTowns.has(townKey);
                    const isExisting = isTownExisting(town.name, town.state);
                    
                    return (
                      <label
                        key={townKey}
                        className={`flex items-start gap-4 p-4 transition-colors ${
                          isExisting 
                            ? 'bg-muted/30 cursor-not-allowed opacity-60' 
                            : `cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-accent/10' : ''}`
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => !isExisting && toggleTown(townKey)}
                          disabled={isExisting}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <MapPin className={`h-4 w-4 shrink-0 ${isExisting ? 'text-muted-foreground' : 'text-primary'}`} />
                            <span className={`font-medium ${isExisting ? 'text-muted-foreground' : ''}`}>{town.name}</span>
                            <span className="text-muted-foreground text-sm">{town.state}</span>
                            {isExisting && (
                              <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full ml-auto shrink-0">
                                Already Added
                              </span>
                            )}
                            {!isExisting && isSelected && (
                              <Check className="h-4 w-4 text-estate-success ml-auto shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {town.description}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <div className="mt-8 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {selectedTowns.size} town{selectedTowns.size !== 1 ? 's' : ''} selected
          </p>
          <Button
            variant="gold"
            size="lg"
            onClick={saveTowns}
            disabled={saving || selectedTowns.size === 0}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Add {selectedTowns.size} Town{selectedTowns.size !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}